import { Input } from '@aigc/antd';
import { FormText, NonEmpty } from '@aigc/components';
import { useStateChange } from '@aigc/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { App, Button, Form, Image, Tag } from 'antd';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { FormControl } from '../../components/form-control';
import { ProductImageSelectionModal } from '../../components/product-image-selection-modal';
import { Permission } from '../../features/common/permision';
import type { UploadFileOutputDto } from '../../features/file/model';
import { fissionScriptMutationOptins } from '../../features/fission/api';
import { FissionStatus } from '../../features/fission/components/fission-status';
import { Scene } from '../../features/fission/components/scene';
import type { FissionInputDto } from '../../features/fission/model';
import { useFissionPageData } from '../../features/fission/store';
import { generateProductImageMutaionOptions } from '../../features/image/api';
import { updateScriptMutation } from '../../features/script/api';
import { getOneTaskQueryOptions } from '../../features/task/api';
import { TaskStatus } from '../../features/task/components/task-status';
import {
  TaskStatusCode,
  type ScriptFissionTaskDto,
} from '../../features/task/model';
import { queryClient } from '../../query-client';

export const Fission = () => {
  const { message, notification } = App.useApp();
  const [modalOpen, setModalOpen] = useState(false);

  const [pageData, setPageData] = useFissionPageData();
  const [task_id, setTaskId] = useState(pageData.fission?.id);
  const generateImage = useMutation(generateProductImageMutaionOptions);
  const updateScript = useMutation(updateScriptMutation);

  const [selectedImage, setSelectedImage] =
    useState<UploadFileOutputDto | null>(null);
  const { control, handleSubmit } = useForm({
    resolver: yupResolver(
      object({
        product_name: string().nullable().trim(),
        target_region: string().nullable().trim(),
      }),
    ),
  });

  const handleSelectImage = (image: UploadFileOutputDto) => {
    setSelectedImage(image);
    setModalOpen(false);
  };

  const fission = useMutation(fissionScriptMutationOptins);

  const { data } = useQuery({
    ...getOneTaskQueryOptions<ScriptFissionTaskDto>(task_id),
    refetchInterval: 5000,
  });

  useStateChange([data], ({ current }) => {
    const [script_data] = current;

    if (!script_data) {
      return;
    }

    setPageData((prev) => ({
      ...prev,
      fission: script_data.generate_data?.fission,
    }));

    if (TaskStatus.isSettled(data?.status)) {
      queryClient.invalidateQueries({
        queryKey: ['脚本列表'],
      });
      setTaskId(void 0);
    }
  });

  const handleFission = handleSubmit(async (values) => {
    if (selectedImage && !values.product_name) {
      notification.error({
        title: '错误',
        description: '请输入新产品名',
      });
      return;
    }

    let product_image_filename = selectedImage?.file_url
      ? selectedImage.file_url
      : pageData.product_image_filename;

    if (!product_image_filename) {
      message.warning('缺少产品图，正在自动生成产品图...');
      const productImageData = await generateImage.mutateAsync({
        product_name: pageData.product_name || '',
        product_description: pageData.product_description || '',
        images: pageData.file_list.filter((file) =>
          file.type.startsWith('image/'),
        ),
      });

      product_image_filename = productImageData.filename;

      updateScript.mutateAsync({
        script_id: pageData.id,
        data: {
          product_image_filename,
        },
      });

      setPageData((prev) => ({
        ...prev,
        product_image_filename,
      }));
    }

    const payload: FissionInputDto = {
      id: pageData.id,
      product_name: (values.product_name || pageData.product_name)!,
      product_description: pageData.product_description ?? '',
      product_image_filename: product_image_filename,
      target_region: values.target_region || pageData.target_region,
      prompt: pageData.prompt,
      duration: pageData.duration,
    };

    if (!payload.product_name) {
      message.info('请输入新产品名');
      return;
    }

    lfq('track', 'image.analyze.fission');

    const data = await fission.mutateAsync(payload);

    if (data.status === TaskStatusCode.任务失败) {
      notification.error({
        title: '错误',
        description: data.error,
      });
      return;
    } else {
      setTaskId(data.id);
      message.success('脚本裂变任务创建成功');
    }

    queryClient.invalidateQueries({
      queryKey: ['脚本列表'],
    });
    setPageData((prev) => ({
      ...prev,
      fission: { id: data.id },
    }));
  });

  const result = data ? data.generate_data?.fission : pageData.fission;

  return (
    <div className="flex h-full w-full flex-col divide-y divide-primary-200 overflow-y-auto sm:flex-row sm:divide-x sm:divide-y-0">
      <div className="card flex h-full w-full flex-none flex-col sm:overflow-auto xl:w-[400px]">
        <h3 className="text-lg font-semibold text-primary-500">
          📦 当前产品图
        </h3>
        {selectedImage ? (
          <div className="my-4 rounded-xl border-2 border-primary-200 p-4 text-center">
            <img
              src={selectedImage.file_url}
              alt="产品图"
              className="mb-3 max-h-[250px] max-w-full rounded-lg object-contain"
            />
            <p className="mb-4 truncate text-sm text-primary-500">
              {selectedImage.filename}
            </p>
            <Button
              onClick={() => setModalOpen(true)}
              color="primary"
              variant="solid"
              block
            >
              🖼️ 选择产品图
            </Button>
          </div>
        ) : (
          <div className="my-4 rounded-xl border-2 border-dashed border-primary-200 p-8 text-center">
            <p className="mb-4 text-primary-500">未选择产品图</p>
            <Button
              onClick={() => setModalOpen(true)}
              color="primary"
              variant="solid"
              block
            >
              🖼️ 选择产品图
            </Button>
          </div>
        )}
        <Form layout="vertical">
          <FormControl
            control={control}
            name="product_name"
            label="新产品名"
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder="请输入新产品名"
                ></Input>
              );
            }}
          ></FormControl>
          <FormControl
            control={control}
            name="target_region"
            label="目标地域"
            render={({ field }) => {
              return (
                <Input
                  {...field}
                  value={field.value ?? ''}
                  placeholder="请输入目标地域"
                ></Input>
              );
            }}
          ></FormControl>
          <Button
            size="large"
            variant="solid"
            color="primary"
            block
            loading={fission.isPending || generateImage.isPending}
            onClick={() => {
              handleFission();
            }}
          >
            {generateImage.isPending ? '生成产品图中' : '脚本裂变'}
          </Button>
        </Form>

        {/* 原产品信息 */}
        <h3 className="my-4 text-lg font-semibold text-primary-500">
          📝 原产品信息
        </h3>
        <Form layout="vertical">
          <Form.Item label="产品名称">
            <FormText>{pageData.product_name}</FormText>
          </Form.Item>
          {pageData.target_region && (
            <Form.Item label="目标地域">
              <FormText>{pageData.target_region}</FormText>
            </Form.Item>
          )}
          {pageData.product_image_filename && (
            <Form.Item label="原产品图">
              <img
                src={pageData.product_image_filename}
                alt="产品图"
                className="max-w-[100%] rounded-lg border border-primary-200 object-contain"
              />
            </Form.Item>
          )}
          {pageData.product_description && (
            <Form.Item label="产品描述">
              <FormText>{pageData.product_description}</FormText>
            </Form.Item>
          )}
          {pageData.duration && (
            <Form.Item label="视频时长">
              <FormText>{pageData.duration}</FormText>
            </Form.Item>
          )}
          {!!pageData.segments?.length && (
            <Form.Item label="提示词">
              <FormText>{pageData.prompt}</FormText>
            </Form.Item>
          )}
        </Form>
      </div>

      <div className="flex flex-none flex-col overflow-y-auto sm:flex-auto">
        <div className="flex-none p-4 text-xl font-bold">📊 裂变内容</div>
        {result ? (
          <div className="card min-h-0 flex-auto animate-slide-up space-y-6 overflow-auto">
            <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div className="w-full min-w-0">
                <h3 className="mb-4 overflow-hidden text-ellipsis break-words text-xl font-bold">
                  {result.product_name}
                </h3>
                <div className="flex gap-2">
                  <NonEmpty
                    value={result.created_at}
                    render={(v) => (
                      <Tag>创建时间：{new Date(v).toLocaleString()}</Tag>
                    )}
                  ></NonEmpty>
                  <NonEmpty
                    value={result.target_region}
                    render={(v) => <Tag>目标地域：{v}</Tag>}
                  ></NonEmpty>
                </div>
                {selectedImage && (
                  <div className="mt-3">
                    <Image
                      src={selectedImage.file_url}
                      alt="产品图"
                      width={240}
                      height={240}
                      classNames={{ image: 'object-contain' }}
                    ></Image>
                  </div>
                )}
              </div>
            </div>

            {result.product_description && (
              <div className="rounded-xl border border-primary-200 bg-white p-6">
                <h4 className="mb-4 text-lg font-semibold">
                  🎨 产品可视化描述
                </h4>
                <p className="whitespace-pre-wrap break-words leading-relaxed">
                  {result.product_description}
                </p>
              </div>
            )}

            {!!result.scenes?.length && (
              <div className="space-y-6">
                <h4 className="text-lg font-semibold">🎬 产品场景列表</h4>
                {result.scenes.map((script, index) => (
                  <Scene
                    key={script.id}
                    index={index}
                    script={script}
                    isPending={fission.isPending}
                  ></Scene>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="mb-4 text-6xl opacity-50">⚡</div>
            <FissionStatus task={data}></FissionStatus>
          </div>
        )}
      </div>

      <ProductImageSelectionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleSelectImage}
        currentImage={selectedImage}
        defaultImage={pageData.product_image_filename}
      />
    </div>
  );
};

export const Route = createFileRoute('/_creator-center/fission')({
  staticData: { permissions: [Permission.视频生成] },
  component: Fission,
});
